import { CancellationStepContentProps } from "./types";
import {
  WSElement,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";

export const AOICancellationAreYouSureInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Are you sure you want to cancel?
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        Please review the information about Wingspan Occupational Accident
        Insurance (OAI) and the impact assessment for cancelling the insurance
        below before proceeding.
      </WSText.ParagraphSm>
    </WSElement>

    <WSInfoBox icon="bulb">
      Protect yourself from the costs of work-related accidents. OAI provides
      benefits for medical expenses and lost income if you are injured while
      performing services paid through the Wingspan platform. As an independent
      contractor, your personal health insurance may deny work-related claims.
    </WSInfoBox>

    <WSInfoBox
      title="Impact assessment"
      icon="alert-circle-fill"
      colorBackground="amber50"
    >
      <WSText.ParagraphSm color="gray600">
        Remember, having worker injury insurance is a critical layer of personal
        protection — especially for independent contractors who face physical,
        field-based, or unpredictable work environments. By cancelling and
        working without coverage:
      </WSText.ParagraphSm>
      <WSText.ParagraphSm color="gray600">
        You may be financially responsible for medical costs and lost income if
        you're injured on the job. There’s no safety net to help you recover or
        stay afloat while unable to work.
      </WSText.ParagraphSm>
      <WSText.ParagraphSm color="gray600">
        Accidents don’t wait for coverage to be required — they happen when you
        least expect it. Canceling your protection removes one of the few
        systems in place to support you in case something goes wrong. Please
        review how this could affect your protection and peace of mind before
        proceeding.
      </WSText.ParagraphSm>
    </WSInfoBox>
  </WSList>
);

export const AOICancellationContinuingInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Continue with cancellation
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        If you wish to proceed and submit a cancellation, here’s what to expect:
      </WSText.ParagraphSm>
    </WSElement>
    <WSInfoBox title="Submit a cancellation" icon="calendar">
      As mentioned in your enrollment, your cancellation will take effect 30
      days after a cancellation submission. If you decide to proceed to submit a
      cancellation, you will be covered—and premiums will be deducted—for all
      payments processed during this period.
    </WSInfoBox>

    <WSInfoBox title="Can I re-enroll?" icon="bulb">
      Yes — re-enrollment is possible if your coverage has been cancelled.
      However, any gap in coverage may impact your compliance and could affect
      your ability to perform client work during that period. We recommend
      reviewing your impact assessment and checking your client requirements
      before proceeding.
    </WSInfoBox>
  </WSList>
);

export const AOICancellationConfirmationInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Confirm & submit cancellation
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        You're about to cancel your coverage. Please confirm that you understand
        the following:
      </WSText.ParagraphSm>
    </WSElement>

    <WSInfoBox icon="accept">
      <WSText.ParagraphSm color="gray500">
        I understand and agree that:
        <ul>
          <li>
            My coverage will remain active for 30 days after cancellation is
            submitted, I will be covered—and premiums will be deducted—for all
            payments processed during this period.
          </li>
          <li>
            After the 30-day period, I will no longer be protected for losses
            due to on-the-job accidents.
          </li>
          <li>
            This change may affect my eligibility for certain client projects.
          </li>
        </ul>
      </WSText.ParagraphSm>
    </WSInfoBox>
  </WSList>
);
